import { L1ObjectObjective } from 'core/l1Object/L1Object';
import { RtbCampaignPlanType } from 'core/rtbCampaign/RtbCampaign';
import {
  CampaignAdTypeState,
  ChannelGroupBrand,
  ChannelGroupStatus,
  ChannelType,
  DynamicCampaignAdTypeSetting,
  NameI18n,
  DeliveryUnit,
  ScreenProperty
} from './ChannelGroup';
import i18n from 'i18n';

// ============ Types ============

export type BannerTypeInput = {
  type: number;
  nameKey: string;
};

export type StaticCreativeTypeSetting = {
  bannerTypeId: number;
  name: string;
  channelGroupId: number;
};

export type StaticAdTypeSetting = {
  adTypeId?: undefined;
  pricingModel?: RtbCampaignPlanType[];
  name: string;
  nameI18n?: NameI18n;
  channelGroupId: number;
  bannerTypes: StaticCreativeTypeSetting[];
  state: CampaignAdTypeState;
  screenDisplayType?: never;
};

export type CampaignAdTypeSetting =
  | DynamicCampaignAdTypeSetting
  | StaticAdTypeSetting;

export type ChannelGroupConfig = {
  id: number;
  name: string;
  addonName: string;
  channelType: ChannelType;
  /**
   * ECOPD-5566: 渠道啟用 / 停用狀態。停用（ARCHIVED）的渠道**照樣註冊**進
   * `ChannelGroupSetting`（既有的群組 / 活動 / 素材才看得到），過濾改在各「可選清單」
   * 出口用 `ChannelGroupSetting.activeValues()` 做。static 渠道由 builder 補
   * ACTIVE，避免讀到 undefined 而誤判。
   */
  status: ChannelGroupStatus;
  /**
   * ECOPD-5572: PMax 自停旗標與合併判定，由 `ChannelGroupSetting.register` 從後端 DTO
   * 正規化（`?? false`）後放進來，所以這一層是**必填布林**、不是 optional ——
   * 讀取端不必再處理 undefined。
   *
   * `pmaxArchived` 只給「按鈕該顯示停用還是重新啟用」用；
   * 判斷渠道是否停用一律看 `effectiveArchived`（後端算好的合併判定）。
   */
  pmaxArchived: boolean;
  effectiveArchived: boolean;
  defaultMargin?: number;
  nonExpressPassCategories?: string[];
  // 戶外渠道的品牌設定，由 register() 從 property.addonProps.brand 拉上來。
  // static 渠道沒有這一項。
  brand?: ChannelGroupBrand;
  campaignAdTypeSettings: CampaignAdTypeSetting[];
  objectives: L1ObjectObjective[];
  deliveryUnit?: DeliveryUnit;
  needExpressPass?: boolean;
  screenProperty?: ScreenProperty;
  // ECOPD-5471: 此渠道支援的影片編碼（H264/H265）。由 ChannelGroupDTO 頂層欄位帶入
  // （見 ChannelGroupSetting.register 的 `...channelGroup` 展開），驅動素材頁的上傳提示。
  allowedVideoCodecs?: string[];
  addonFeatures: {
    report: {
      [key: string]: boolean;
    };
    campaign: {
      [key: string]: boolean;
    };
  };
};

// ============ Builder Classes ============

export class AdTypeSettingBuilder {
  private bannerTypes: BannerTypeInput[] = [];
  private adTypeState = CampaignAdTypeState.ACTIVE;
  private adTypeNameI18n?: NameI18n;
  private pricingModels: RtbCampaignPlanType[] = [];

  constructor(private adTypeName: string) {}

  withBannerTypes(...types: BannerTypeInput[]): this {
    this.bannerTypes = types;
    return this;
  }

  withPricingModels(...models: RtbCampaignPlanType[]): this {
    this.pricingModels = models;
    return this;
  }

  withNameI18n(nameI18n: NameI18n): this {
    this.adTypeNameI18n = nameI18n;
    return this;
  }

  withState(state: CampaignAdTypeState): this {
    this.adTypeState = state;
    return this;
  }

  build(channelGroupId: number): StaticAdTypeSetting {
    return {
      name: this.adTypeName,
      nameI18n: this.adTypeNameI18n,
      channelGroupId,
      state: this.adTypeState,
      pricingModel: this.pricingModels,
      bannerTypes: this.bannerTypes.map((bt) => ({
        bannerTypeId: bt.type,
        name: i18n.t(bt.nameKey),
        channelGroupId
      }))
    };
  }
}

export class ChannelGroupConfigBuilder {
  private _name = '';
  private _addonName = '';
  private _channelType: ChannelType = ChannelType.OUTDOOR;
  private _defaultMargin?: number;
  private _needExpressPass?: boolean;
  private _objectives: L1ObjectObjective[] = [];
  private _adTypeBuilders: AdTypeSettingBuilder[] = [];

  constructor(private _id: number) {}

  name(name: string): this {
    this._name = name;
    return this;
  }

  addon(addonName: string): this {
    this._addonName = addonName;
    return this;
  }

  channelType(type: ChannelType): this {
    this._channelType = type;
    return this;
  }

  margin(value: number): this {
    this._defaultMargin = value;
    return this;
  }

  expressPass(): this {
    this._needExpressPass = true;
    return this;
  }

  objectives(...objs: L1ObjectObjective[]): this {
    this._objectives = objs;
    return this;
  }

  adTypes(...builders: AdTypeSettingBuilder[]): this {
    this._adTypeBuilders = builders;
    return this;
  }

  build(key: string): { key: string; config: ChannelGroupConfig } {
    return {
      key,
      config: {
        id: this._id,
        name: this._name,
        addonName: this._addonName,
        channelType: this._channelType,
        defaultMargin: this._defaultMargin,
        needExpressPass: this._needExpressPass,
        objectives: this._objectives,
        campaignAdTypeSettings: this._adTypeBuilders.map((b) =>
          b.build(this._id)
        ),
        // static 渠道不經過後端 DTO，狀態恆為啟用
        status: ChannelGroupStatus.ACTIVE,
        // ECOPD-5572: static 渠道（RTB / FB / Message）不走 `register`，
        // 這裡不補預設值的話 config 上就是 undefined，判定會讀到 undefined 而非 false。
        // 這些渠道也不可能被 PMax 自停（沒有對應的後端渠道記錄可停）。
        pmaxArchived: false,
        effectiveArchived: false,
        addonFeatures: { report: {}, campaign: {} }
      }
    };
  }
}

// ============ Factory Functions ============

export const channelGroup = (id: number) => new ChannelGroupConfigBuilder(id);

export const adType = (name: string) => new AdTypeSettingBuilder(name);

export const banner = (type: number, nameKey: string): BannerTypeInput => ({
  type,
  nameKey
});
